<template>
  <ScanLine session-key="scan:triage" />

  <section ref="screenRoot" class="page triage" data-test="triage-screen">
    <div class="hero">
      <div>
        <Eyebrow class="anim" data-delay="80" pulse>
          Live · {{ liveTimestamp }}
        </Eyebrow>
        <DisplayHeading class="anim" data-delay="140" lead="Project" accent="triage" />
        <p class="display-sub anim" data-delay="200">
          {{ subline }} Tap a row to open its scorecard.
        </p>
      </div>
      <div v-if="hasHero || hasCapexHero" class="hero-stats anim" data-delay="240">
        <div v-if="hasHero" class="hero-stat" data-test="hero-missing-sp">
          <span class="lbl-mono">Avg missing SP</span>
          <span class="value">
            <CountUp :to="avgMissingSp.value" suffix="%" />
          </span>
          <span class="delta">{{ avgMissingSp.label }}</span>
        </div>
        <div v-if="hasCapexHero" class="hero-stat" data-test="hero-capex">
          <span class="lbl-mono">Avg CapEx</span>
          <span class="value">
            <CountUp :to="avgCapex.value" suffix="%" />
          </span>
          <span class="delta">{{ avgCapex.label }}</span>
        </div>
      </div>
    </div>

    <div class="strip anim" data-delay="280">
      <button
        v-for="f in filters"
        :key="f.id"
        type="button"
        :class="['chip', { active: activeFilter === f.id }]"
        @click="activeFilter = f.id"
      >
        {{ f.label }} <span class="count">{{ f.count }}</span>
      </button>
      <span class="spacer" />
      <label class="search" :class="{ active: query.length > 0 }">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" />
          <path d="M10.5 10.5 L14 14" stroke-linecap="round" />
        </svg>
        <input
          v-model="query"
          type="search"
          placeholder="Search board or sprint…"
          aria-label="Search projects"
          data-test="triage-search"
          @keydown.escape="query = ''"
        />
        <button
          v-if="query.length > 0"
          type="button"
          class="search-clear"
          aria-label="Clear search"
          data-test="triage-search-clear"
          @click="query = ''"
        >×</button>
      </label>
      <span class="sort">Sort · Hygiene risk ↓</span>
    </div>

    <div v-if="store.error" class="error anim" data-delay="340" data-test="error">
      <p class="error-title">Couldn't load projects</p>
      <p class="error-msg">{{ store.error.message }}</p>
      <button type="button" class="retry" @click="store.load(true)">Retry</button>
    </div>

    <div
      v-else-if="store.loading && store.items.length === 0"
      class="skeletons anim"
      data-delay="340"
      data-test="loading"
    >
      <div v-for="i in 3" :key="i" class="skel-row" />
    </div>

    <div v-else-if="visibleRows.length" class="list" data-test="projects-table">
      <div
        v-for="(group, gIdx) in groupedRows"
        :key="group.key"
        class="proj-group"
        :data-test="`proj-group-${group.key}`"
      >
        <div class="group-head anim" :data-delay="340 + gIdx * 60">
          <button
            type="button"
            class="group-toggle"
            :class="{ collapsed: isCollapsed(group.key) }"
            :aria-expanded="!isCollapsed(group.key)"
            :aria-controls="`proj-group-body-${group.key}`"
            :title="`${isCollapsed(group.key) ? 'Expand' : 'Collapse'} ${group.key}`"
            :data-test="`proj-group-toggle-${group.key}`"
            @click="toggleGroup(group.key)"
          >
            <svg class="caret" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
              <path d="M6 4l5 4-5 4" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <a
            class="group-key"
            :href="buildProjectUrl(group.key)"
            target="_blank"
            rel="noopener noreferrer"
            :title="`Open project ${group.key} in Jira`"
            :data-test="`proj-group-jira-${group.key}`"
          >
            {{ group.key }} <span class="ext" aria-hidden="true">↗</span>
          </a>
          <button
            type="button"
            class="group-count"
            :data-test="`proj-group-count-${group.key}`"
            @click="toggleGroup(group.key)"
          >
            {{ group.count }} board{{ group.count === 1 ? "" : "s" }}
          </button>
        </div>

        <div
          v-show="!isCollapsed(group.key)"
          :id="`proj-group-body-${group.key}`"
          class="group-body"
        >
        <DataRow
          v-for="(row, idx) in group.rows"
          :key="row.rowId"
          :risk="row.risk"
          :delay="360 + gIdx * 60 + idx * 70"
          :data-test="`proj-row-${row.rowId}`"
          @open="openRow(row)"
        >
          <div class="rank">
            <span>RANK</span><b>{{ String(idx + 1).padStart(2, "0") }}</b>
          </div>

        <div class="head">
          <div class="key">
            {{ row.key }} · {{ row.boardName || (row.isKanban ? "Kanban" : "Sprints") }}
            <RiskPill :variant="row.signal.variant" :data-test="`proj-signal-${row.rowId}`">
              {{ row.signal.label }}
            </RiskPill>
            <BoardTypeBadge v-if="row.isKanban" board-type="kanban" />
            <RiskPill v-else variant="live" :state="row.isActive ? 'active' : 'closed'">
              {{ row.isActive ? "Active" : "Closed" }}
            </RiskPill>
          </div>
          <div class="name">{{ row.sprintName }}</div>
          <div class="meta">
            <span v-for="(m, i) in row.metaParts" :key="i" v-html="m" />
          </div>
          <div class="actions">
            <a
              :href="row.jiraUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="ghost"
              :title="row.jiraTitle"
              :data-test="`proj-jira-${row.rowId}`"
              @click.stop
            >Open in Jira ↗</a>
          </div>
        </div>

        <div class="metrics">
          <MetricCell
            label="Missing SP"
            :value="fmtPct(row.lastSprint?.m17_missing_estimates_pct)"
            :bar="pctBar(row.lastSprint?.m17_missing_estimates_pct)"
            :state="severityFor(row.lastSprint?.m17_missing_estimates_pct, 0.5, 0.2)"
            tip="Share of sprint tickets without story points"
          />
          <MetricCell
            label="Start ready"
            :value="row.lastSprint?.m18_start_readiness === true ? 'Yes' : row.lastSprint?.m18_start_readiness === false ? 'No' : '—'"
            :state="row.lastSprint?.m18_start_readiness === true ? 'ok' : row.lastSprint?.m18_start_readiness === false ? 'bad' : 'neutral'"
            tip="All sprint tickets had SP & priority on day 0"
          />
          <MetricCell
            label="Coverage"
            :value="fmtPct(row.lastSprint?.coverage?.pct)"
            :bar="row.lastSprint?.coverage?.pct == null ? undefined : Math.round(row.lastSprint.coverage.pct * 100)"
            :state="severityFor(row.lastSprint?.coverage?.pct, 0.4, 0.7, true)"
            tip="Share of tickets with start + end dates set"
          />
          <MetricCell
            label="Escalations"
            :value="(row.lastSprint?.escalation_count ?? 0) > 0 ? String(row.lastSprint?.escalation_count) : '—'"
            :state="(row.lastSprint?.escalation_count ?? 0) > 0 ? 'bad' : 'neutral'"
            tip="Unplanned firefighting added in-flight"
          />
          <MetricCell
            label="CapEx"
            :value="fmtPct(capexShare(row.lastSprint))"
            :bar="pctBar(capexShare(row.lastSprint))"
            state="neutral"
            tip="Capitalizable share of last sprint's tickets (by count). '—' when the board's Epics aren't classified for capitalization."
          />
        </div>

        <div class="right">
          <div class="sprints">
            <b><CountUp :to="row.sprint_count" /></b>
            <span>{{ row.isKanban ? "Window" : "Sprints" }}</span>
          </div>
          <div class="arrow">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
              <path d="M6 3l5 5-5 5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>
        </DataRow>
        </div>
      </div>
    </div>

    <div
      v-else-if="ranked.length > 0"
      class="empty anim"
      data-delay="340"
      data-test="empty-search"
    >
      No boards match "{{ query }}" in the
      <strong>{{ activeFilter === "all" ? "current" : filterLabel(activeFilter) }}</strong>
      view.
      <button type="button" class="link" @click="query = ''">Clear search</button>
    </div>
    <div v-else class="empty anim" data-delay="340">
      No projects yet. Run an onboarding flow to populate.
    </div>

    <div class="footnote anim" data-delay="600">
      <span>Synced {{ liveTimestamp }}</span>
      <span class="dotsep" />
      <span>Provisional thresholds</span>
      <span class="dotsep" />
      <span>Cadence 7-day</span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import BoardTypeBadge from "@/components/BoardTypeBadge.vue";
import CountUp from "@/components/scorecard/CountUp.vue";
import DataRow from "@/components/scorecard/DataRow.vue";
import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import Eyebrow from "@/components/scorecard/Eyebrow.vue";
import MetricCell from "@/components/scorecard/MetricCell.vue";
import RiskPill from "@/components/scorecard/RiskPill.vue";
import ScanLine from "@/components/scorecard/ScanLine.vue";
import { useReveal } from "@/composables/useReveal";
import { buildProjectSprintUrl, buildProjectUrl } from "@/lib/jiraJql";
import { local } from "@/lib/storage";
import type { ProjectListItem, ProjectLastSprint } from "@/api/types";
import { useProjectsStore } from "@/stores/projects";

type FilterId = "all" | "risk" | "active" | "closed";

interface RankedRow {
  key: string;
  boardType: "scrum" | "kanban";
  // Stable per-board id — a project with both boards yields two rows that
  // share `key`, so v-for :key and data-test must use rowId, not key.
  rowId: string;
  boardId: number | null;
  boardName: string;
  isKanban: boolean;
  sprint_count: number;
  lastSprint?: ProjectLastSprint;
  sprintName: string;
  metaParts: string[];
  // Health pill. The old single "Low signal" tag conflated two opposite
  // situations — too few tickets to score vs. a well-populated board with poor
  // date coverage — so it's split into explicit labels here.
  signal: { variant: "high" | "med" | "low" | "neutral"; label: string };
  isActive: boolean;
  risk: "high" | "med" | "low";
  riskScore: number;
  jiraUrl: string;
  jiraTitle: string;
}

const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);

const store = useProjectsStore();
const router = useRouter();
const activeFilter = ref<FilterId>("all");
const query = ref("");

// Per-project collapse state for the triage list. Keyed by project key; a key
// in the set is collapsed (boards hidden). Default is expanded so the list
// reads the same as before until a PM chooses to fold a project away. The set
// is persisted to localStorage so folds survive a reload.
const COLLAPSED_KEY = "triage.collapsedProjects";

function loadCollapsed(): Set<string> {
  try {
    const raw = local.get(COLLAPSED_KEY);
    if (!raw) return new Set();
    const arr: unknown = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === "string")) : new Set();
  } catch {
    // Corrupt / unavailable storage — start expanded rather than throwing.
    return new Set();
  }
}

const collapsed = ref<Set<string>>(loadCollapsed());
function isCollapsed(key: string): boolean {
  return collapsed.value.has(key);
}
function toggleGroup(key: string): void {
  const next = new Set(collapsed.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsed.value = next;
  try {
    local.set(COLLAPSED_KEY, JSON.stringify([...next]));
  } catch {
    // Storage full / disabled — collapse still works for this session.
  }
}

onMounted(() => { store.load(); });

function fmtPct(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${Math.round(n * 100)}%`;
}
function pctBar(n: number | null | undefined): number | undefined {
  if (n == null) return undefined;
  return Math.round(n * 100);
}
// Count-based CapEx share of the board's last sprint (parity with what finance
// reports today). null (renders "—") when the tenant has no Capatizable field
// (not applicable) or the sprint has no classifiable tickets — never imply 0%
// capitalizable.
function capexShare(ls: ProjectLastSprint | undefined): number | null {
  const c = ls?.capitalization;
  if (!c || !c.applicable) return null;
  return c.by_count.pct;
}
function severityFor(
  value: number | null | undefined,
  badAt: number,
  warnAt: number,
  invert = false,
): "bad" | "warn" | "ok" | "neutral" {
  if (value == null) return "neutral";
  const v = invert ? 1 - value : value;
  if (v >= badAt) return "bad";
  if (v >= warnAt) return "warn";
  return "ok";
}

function rank(row: ProjectListItem): RankedRow {
  const ls = row.last_sprint;
  const boardType: "scrum" | "kanban" =
    (row.board_type ?? ls?.board_type ?? "scrum") === "kanban" ? "kanban" : "scrum";
  const isKanban = boardType === "kanban";
  let risk = 0;
  if (ls?.low_signal) risk += 100;
  if (ls?.m18_start_readiness === false) risk += 30;
  if (ls?.m17_missing_estimates_pct != null) risk += ls.m17_missing_estimates_pct * 100;
  if ((ls?.escalation_count ?? 0) > 0) risk += 10 + Math.min(ls?.escalation_count ?? 0, 5) * 5;
  if (ls?.coverage?.pct != null) risk += (1 - ls.coverage.pct) * 50;

  let riskBand: "high" | "med" | "low" = "low";
  if (risk >= 80) riskBand = "high";
  else if (risk >= 30) riskBand = "med";

  // Health pill. `low_signal` is set when a sprint has < 5 tickets OR < 50%
  // date coverage — two opposite stories the old single "Low signal" tag
  // conflated. Split them: too-few-tickets is un-scoreable noise (muted,
  // dismissible); enough-tickets-but-low-coverage is a real, actionable
  // hygiene gap (red). When low_signal isn't set, fall back to the risk band.
  const tickets = ls?.coverage?.total_count;
  const coveragePct = ls?.coverage?.pct;
  let signal: RankedRow["signal"];
  if (ls?.low_signal === true) {
    if (tickets != null && tickets < 5) {
      signal = { variant: "neutral", label: "Insufficient data <5" };
    } else if (coveragePct != null && coveragePct < 0.5) {
      signal = { variant: "high", label: "Low coverage <50%" };
    } else {
      // Flagged low-signal but the cause isn't visible (legacy row) — generic.
      signal = { variant: "high", label: "Low signal" };
    }
  } else if (riskBand === "high") {
    signal = { variant: "high", label: "At risk" };
  } else if (riskBand === "med") {
    signal = { variant: "med", label: "Watch" };
  } else {
    signal = { variant: "low", label: "Stable" };
  }

  const isActive = ls?.sprint_state === "active";

  // Meta line — tries to keep parity with the prototype's three-bullet layout.
  const meta: string[] = [];
  if (ls) {
    if (isActive) {
      meta.push("<span><b>In progress</b></span>");
    } else if (ls.sprint_state) {
      const cap = ls.sprint_state.charAt(0).toUpperCase() + ls.sprint_state.slice(1);
      meta.push(`<span>${cap}</span>`);
    }
    if (ls.predicted_carryover?.applicable && ls.predicted_carryover.sp > 0) {
      meta.push(`<span>${ls.predicted_carryover.sp} SP carryover predicted</span>`);
    }
    if (ls.coverage?.pct != null) {
      meta.push(`<span>${Math.round(ls.coverage.pct * 100)}% coverage</span>`);
    }
    if (ls.sprint_end) {
      try {
        const d = new Date(ls.sprint_end);
        meta.push(`<span>${isActive ? "Closes" : "Closed"} ${d.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</span>`);
      } catch { /* ignore unparseable date */ }
    }
  } else {
    meta.push("<span>No sprints yet</span>");
  }

  const jiraUrl = buildProjectSprintUrl(row.key, {
    sprintId: ls?.sprint_id ?? null,
    isActive,
  });
  const jiraTitle = ls
    ? `Open ${row.key} · ${ls.sprint_name} in Jira`
    : `Open ${row.key} in Jira`;

  return {
    key: row.key,
    boardType,
    // Board-first: /projects now returns one row per (key, board_id), so a
    // project can have several same-type boards. Key on board_id when present
    // (falling back to board_type for legacy rows) to avoid v-for collisions.
    rowId: row.board_id != null ? `${row.key}-b${row.board_id}` : `${row.key}-${boardType}`,
    boardId: row.board_id ?? null,
    boardName: row.board_name ?? "",
    isKanban,
    sprint_count: row.sprint_count,
    lastSprint: ls,
    sprintName: ls?.sprint_name ?? row.key,
    metaParts: meta,
    signal,
    isActive,
    risk: riskBand,
    riskScore: risk,
    jiraUrl,
    jiraTitle,
  };
}

const ranked = computed<RankedRow[]>(() =>
  store.items.map(rank).sort((a, b) => b.riskScore - a.riskScore),
);

// Board ids whose sprints span more than one project key — opening these
// "raw" lands on the per-project breakdown, so rows on them get focused.
const sharedBoardIds = computed<Set<number>>(() => {
  const byBoard = new Map<number, Set<string>>();
  for (const p of store.items) {
    if (p.board_id == null) continue;
    const keys = byBoard.get(p.board_id) ?? new Set<string>();
    keys.add(p.key);
    byBoard.set(p.board_id, keys);
  }
  const shared = new Set<number>();
  for (const [bid, keys] of byBoard) if (keys.size > 1) shared.add(bid);
  return shared;
});

const chipFilteredRows = computed<RankedRow[]>(() => {
  switch (activeFilter.value) {
    case "risk":   return ranked.value.filter((r) => r.risk !== "low");
    case "active": return ranked.value.filter((r) => r.isActive);
    // A Kanban rolling window is neither active nor closed — keep it out of
    // the sprint-only "Closed" view.
    case "closed": return ranked.value.filter((r) => !r.isActive && !r.isKanban);
    default:       return ranked.value;
  }
});

// Case-insensitive substring match against the project key (e.g. "DA") and
// the latest sprint name (e.g. "AI Sprint 3 - Q2") — covers both ways a
// PM looks up a board. Search composes on top of the active chip rather
// than overriding it, so "Active + foo" stays a valid intersection.
const visibleRows = computed<RankedRow[]>(() => {
  const q = query.value.trim().toLowerCase();
  const base = chipFilteredRows.value;
  if (!q) return base;
  return base.filter((r) =>
    r.key.toLowerCase().includes(q) || r.sprintName.toLowerCase().includes(q),
  );
});

// A project owns one or more boards (OC alone runs 3). The triage list groups
// the visible board rows under their project so a PM sees "OC · 3 boards" and
// every board beneath it. Group order follows the project's WORST board so the
// most at-risk projects still float to the top — worst-first survives grouping.
//
// Within a group, sprint STATE leads hygiene risk: the ACTIVE sprint comes
// first, then closed sprints, then a Kanban rolling window. The active sprint
// is the one still in flight and the only one a PM can actually fix, so it
// surfaces at the top of its project regardless of how the finalised closed
// scores compare. Risk stays the tiebreaker inside each state bucket — and
// since `visibleRows` is already risk-sorted, a stable sort by state preserves
// that order within each bucket.
interface BoardGroup {
  key: string;
  count: number;
  worstScore: number;
  rows: RankedRow[];
}

// Active (0) → Closed (1) → Kanban window (2).
function stateRank(r: RankedRow): number {
  if (r.isKanban) return 2;
  return r.isActive ? 0 : 1;
}

const groupedRows = computed<BoardGroup[]>(() => {
  const byKey = new Map<string, RankedRow[]>();
  for (const row of visibleRows.value) {
    const bucket = byKey.get(row.key);
    if (bucket) bucket.push(row);
    else byKey.set(row.key, [row]);
  }
  return [...byKey.entries()]
    .map(([key, rows]) => ({
      key,
      count: rows.length,
      worstScore: Math.max(...rows.map((r) => r.riskScore)),
      rows: rows.slice().sort((a, b) => stateRank(a) - stateRank(b)),
    }))
    .sort((a, b) => b.worstScore - a.worstScore);
});

const filters = computed<{ id: FilterId; label: string; count: number }[]>(() => {
  const all = ranked.value;
  return [
    { id: "all",    label: "All projects", count: all.length },
    { id: "risk",   label: "At risk",      count: all.filter((r) => r.risk !== "low").length },
    { id: "active", label: "Active",       count: all.filter((r) => r.isActive).length },
    { id: "closed", label: "Closed",       count: all.filter((r) => !r.isActive && !r.isKanban).length },
  ];
});

// Hero stat scopes to the filtered view so the headline matches what's
// actually rendered below it. With no chip selected ("All projects") this
// is identical to the full ranked list. Server-side authz already restricts
// `ranked` to the projects the caller is allowed to see (Lambda filters
// /projects by the JWT's custom:projects claim).
const hasHero = computed(() =>
  visibleRows.value.some((r) => r.lastSprint?.m17_missing_estimates_pct != null),
);

const avgMissingSp = computed<{ value: number; label: string }>(() => {
  const present = visibleRows.value
    .map((r) => r.lastSprint?.m17_missing_estimates_pct)
    .filter((v): v is number => v != null);
  if (present.length === 0) return { value: 0, label: "Awaiting sprints" };
  const avg = present.reduce((s, v) => s + v, 0) / present.length;
  const scope = activeFilter.value === "all"
    ? `Across ${present.length} board${present.length === 1 ? "" : "s"}`
    : `Across ${present.length} ${filterLabel(activeFilter.value)} board${present.length === 1 ? "" : "s"}`;
  return { value: Math.round(avg * 100), label: scope };
});

// Sibling portfolio stat to avgMissingSp: the average CapEx share across the
// visible boards' last sprints. Simple board-average (matching avgMissingSp's
// treatment) — count-based, and only boards whose Epics are classified
// (capexShare returns null otherwise) contribute.
const hasCapexHero = computed(() =>
  visibleRows.value.some((r) => capexShare(r.lastSprint) != null),
);

const avgCapex = computed<{ value: number; label: string }>(() => {
  const present = visibleRows.value
    .map((r) => capexShare(r.lastSprint))
    .filter((v): v is number => v != null);
  if (present.length === 0) return { value: 0, label: "Awaiting sprints" };
  const avg = present.reduce((s, v) => s + v, 0) / present.length;
  const scope = activeFilter.value === "all"
    ? `Across ${present.length} board${present.length === 1 ? "" : "s"}`
    : `Across ${present.length} ${filterLabel(activeFilter.value)} board${present.length === 1 ? "" : "s"}`;
  return { value: Math.round(avg * 100), label: scope };
});

function filterLabel(id: FilterId): string {
  switch (id) {
    case "risk":   return "at-risk";
    case "active": return "active";
    case "closed": return "closed";
    default:       return "";
  }
}

const subline = computed(() => {
  const total = ranked.value.length;
  if (total === 0) return "Onboard a project to start scoring sprints.";
  const projects = new Set(ranked.value.map((r) => r.key)).size;
  return `${total} board${total === 1 ? "" : "s"} across ${projects} project${projects === 1 ? "" : "s"}, grouped by project. Active sprints first, then by hygiene risk.`;
});

const liveTimestamp = computed(() => {
  // Recomputed on first render; cheap to keep static for the screen lifetime.
  const d = new Date();
  const date = d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${time}`;
});

function openRow(row: RankedRow): void {
  // Board-first: open the board's scorecard directly. Legacy rows without a
  // board_id fall back to the project routes. For a SHARED board (sprints span
  // multiple projects), open it focused on this row's project so we land on its
  // scorecard rather than the per-project breakdown grid.
  if (row.boardId != null) {
    const query = sharedBoardIds.value.has(row.boardId) ? { project: row.key } : undefined;
    router.push({ name: "board", params: { boardId: String(row.boardId) }, query });
    return;
  }
  router.push(
    row.isKanban
      ? { name: "project-kanban", params: { key: row.key } }
      : { name: "project", params: { key: row.key } },
  );
}
</script>

<style scoped>
.triage { position: relative; }

.hero {
  display: grid; grid-template-columns: 1fr auto;
  gap: 40px; align-items: end;
  margin-bottom: 44px;
}
.hero-stats { display: flex; gap: 0; align-items: stretch; }
.hero-stat {
  display: grid; gap: 4px;
  text-align: right;
  padding-left: 32px;
  border-left: 1px solid var(--line);
  align-self: stretch;
  padding-top: 8px;
}
.hero-stats .hero-stat + .hero-stat { margin-left: 32px; }
.hero-stat .value {
  font-size: 56px; font-weight: 500;
  letter-spacing: -0.04em; line-height: 1;
  font-variant-numeric: tabular-nums;
}
.hero-stat .delta {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; color: var(--muted);
  margin-top: 4px;
}

.strip {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 4px;
  overflow-x: auto;
}
.strip .spacer { flex: 1; }
.sort {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; color: var(--muted);
  letter-spacing: 0.08em; text-transform: uppercase;
  white-space: nowrap;
}

.search {
  display: inline-flex; align-items: center; gap: 6px;
  height: 28px; padding: 0 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  transition: border-color 120ms ease, color 120ms ease;
}
.search:focus-within,
.search.active {
  border-color: var(--ink-2, #4b5563);
  color: var(--ink, #111827);
}
.search svg { width: 13px; height: 13px; flex-shrink: 0; }
.search input {
  border: 0; outline: 0; background: transparent;
  font: inherit; font-size: 12px;
  color: inherit; width: 160px;
  padding: 0; min-width: 0;
}
.search input::placeholder { color: var(--muted); }
/* Suppress the native clear-search icon Chrome injects — we render our own. */
.search input::-webkit-search-cancel-button { -webkit-appearance: none; appearance: none; }
.search-clear {
  border: 0; background: transparent; cursor: pointer;
  color: var(--muted); font-size: 16px; line-height: 1;
  padding: 0 2px; margin-right: -2px;
}
.search-clear:hover { color: var(--ink, #111827); }

.empty .link {
  border: 0; background: transparent;
  color: var(--ink-2, #2563eb); cursor: pointer;
  font: inherit; padding: 0; margin-left: 6px;
  text-decoration: underline;
}

.list { display: grid; gap: 0; margin-top: 24px; }

.proj-group { display: grid; gap: 0; }
.proj-group + .proj-group { margin-top: 18px; }
.group-head {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 4px 8px 6px;
  border-bottom: 1px solid var(--line);
}
.group-toggle {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; padding: 0;
  border: 0; background: transparent; cursor: pointer;
  color: var(--muted-2); border-radius: 6px;
  transition: color var(--speed-quick) var(--ease), background var(--speed-quick) var(--ease);
}
.group-toggle:hover { color: var(--ink); background: var(--bg-elev); }
.group-toggle .caret {
  width: 13px; height: 13px;
  transform: rotate(90deg);
  transition: transform var(--speed-quick) var(--ease);
}
.group-toggle.collapsed .caret { transform: rotate(0deg); }
.group-key {
  font-size: 14px; font-weight: 600; color: var(--ink);
  letter-spacing: -0.01em; text-decoration: none;
  display: inline-flex; align-items: center; gap: 4px;
  transition: color var(--speed-quick) var(--ease);
}
.group-key:hover { color: var(--brand); }
.group-key .ext {
  font-size: 11px; color: var(--muted-2);
  transition: color var(--speed-quick) var(--ease);
}
.group-key:hover .ext { color: var(--brand); }
.group-count {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; color: var(--muted-2);
  letter-spacing: 0.06em; text-transform: uppercase;
  border: 0; background: transparent; cursor: pointer;
  padding: 2px 4px; border-radius: 6px;
  transition: color var(--speed-quick) var(--ease);
}
.group-count:hover { color: var(--ink); }

.group-body { display: grid; gap: 0; }

.rank {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; color: var(--muted-2);
  letter-spacing: 0.08em;
  padding-left: 14px;
}
.rank b {
  display: block;
  font-size: 22px; color: var(--ink);
  font-weight: 500; letter-spacing: -0.02em;
  margin-top: 2px;
}

.head { display: grid; gap: 6px; min-width: 0; }
.head .key {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.14em;
  color: var(--muted); text-transform: uppercase;
  display: inline-flex; align-items: center; gap: 10px;
  flex-wrap: wrap;
}
.name {
  font-size: 24px; font-weight: 500;
  letter-spacing: -0.025em; line-height: 1.1;
  color: var(--ink);
}
.meta {
  font-size: 13px; color: var(--muted);
  display: flex; gap: 14px; flex-wrap: wrap;
}
.meta :deep(b) { color: var(--ink-2); font-weight: 500; }

.actions { display: flex; gap: 8px; margin-top: 6px; }
.ghost {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
  background: transparent; border: 0;
  padding: 0;
  cursor: pointer;
  border-bottom: 1px solid var(--line);
  transition: color var(--speed-quick) var(--ease),
              border-color var(--speed-quick) var(--ease);
}
.ghost:hover { color: var(--ink); border-color: var(--ink); }
.ghost:focus-visible {
  outline: none;
  color: var(--brand); border-color: var(--brand);
}

.metrics {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
}

.right {
  display: flex; align-items: center;
  justify-content: flex-end; gap: 12px;
}
.sprints { text-align: right; line-height: 1; }
.sprints b {
  display: block;
  font-size: 32px; font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.sprints span {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.14em;
  color: var(--muted); text-transform: uppercase;
}
.arrow {
  width: 36px; height: 36px;
  border-radius: 50%;
  border: 1px solid var(--line);
  display: grid; place-items: center;
  background: var(--bg-elev);
  transition: transform var(--speed-standard) var(--ease),
              border-color var(--speed-standard) var(--ease),
              background var(--speed-standard) var(--ease),
              color var(--speed-standard) var(--ease);
}
.arrow svg { width: 14px; height: 14px; }
:deep(.row:hover) .arrow {
  transform: translateX(4px);
  border-color: var(--ink);
  background: var(--ink);
  color: #fff;
}

.error {
  padding: 16px;
  background: var(--red-soft);
  border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
  border-radius: 12px;
  color: var(--red-ink);
  display: grid; gap: 8px;
  margin-top: 24px;
}
.error-title { font-weight: 500; margin: 0; }
.error-msg { font-size: 13px; margin: 0; color: var(--ink-2); }
.retry {
  align-self: start;
  background: var(--bg-elev);
  border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--red-ink);
}
.retry:hover { border-color: var(--red); }

.skeletons { display: grid; gap: 12px; margin-top: 24px; }
.skel-row {
  height: 96px; border-radius: 12px;
  background: var(--skeleton);
  background-size: 200% 100%;
  animation: shimmer 1.6s linear infinite;
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.empty {
  margin-top: 32px;
  padding: 48px;
  border: 1px dashed var(--line);
  border-radius: 12px;
  color: var(--muted);
  text-align: center;
}

@media (max-width: 1100px) {
  .hero { grid-template-columns: 1fr; }
  .hero-stats { flex-direction: row; gap: 32px; }
  .hero-stat { padding-left: 0; border-left: 0; text-align: left; }
  .hero-stats .hero-stat + .hero-stat { margin-left: 0; }
  .metrics { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .hero-stats { gap: 20px; }
  .hero-stat .value { font-size: 44px; }
}
</style>
